/**
 * Links the main product gallery to its thumbnail strip.
 *
 * Swiper did this declaratively: the main <swiper-container> carried a
 * `thumbs-swiper` attribute and its thumbs module handled clicks, the active
 * class and keeping the active thumb in view. Blaze has no thumbs module, so
 * that wiring lives here. Ported from kinetec's implementation.
 *
 * Forthglade's thumbs sit under the main image and scroll horizontally
 * (data-thumb-positioning="under"), so the vertical scroll-container handling
 * kinetec needs for its side-mounted strip is not carried over. If a layout
 * ever puts the thumbs beside the gallery again, that is the piece to bring in.
 */
const THUMB_DRAG_GUARD_MS = 250;

class ProductGallerySliderController {
  constructor(root = document) {
    this.instances = new WeakMap();
    this.init(root);
  }

  init(root = document) {
    const scope = root && typeof root.querySelectorAll === "function" ? root : document;
    Array.from(scope.querySelectorAll("[gallery-container]")).forEach((container) =>
      this.initContainer(container),
    );
  }

  initContainer(container) {
    if (!container || this.instances.has(container)) return;

    const main = container.querySelector("[main-gallery-slider]");
    const thumbs = container.querySelector("[data-thumb-gallery-slider]");

    if (!main) {
      this.markReady(container);
      return;
    }

    // A gallery with a single image has no thumb strip, and still has to be revealed.
    if (!thumbs) {
      this.bindMain(container, main, null, []);
      return;
    }

    const thumbTrackContainer = thumbs.querySelector(".blaze-track-container");
    const thumbSlides = Array.from(thumbs.querySelectorAll(".thumb"));

    const onThumbClick = (event) => {
      // Blaze finishes a drag with a click on whatever was under the pointer, so a
      // drag that ends on a thumb would otherwise select it.
      const draggedAt = Number(thumbTrackContainer?.dataset?.thumbDraggedAt || 0);
      if (draggedAt && Date.now() - draggedAt < THUMB_DRAG_GUARD_MS) return;

      const target = event.currentTarget;
      const index = Number(target?.dataset?.index ?? thumbSlides.indexOf(target));
      if (!Number.isFinite(index)) return;

      main.slider?.slideTo?.(index, 0);
    };

    const onThumbKeydown = (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      onThumbClick(event);
    };

    thumbSlides.forEach((slide, index) => {
      if (!slide.dataset.index) slide.dataset.index = String(index);
      if (!slide.hasAttribute("tabindex")) slide.setAttribute("tabindex", "0");
      if (!slide.hasAttribute("role")) slide.setAttribute("role", "button");
      slide.addEventListener("click", onThumbClick);
      slide.addEventListener("keydown", onThumbKeydown);
    });

    this.bindMain(container, main, thumbs, thumbSlides, { onThumbClick, onThumbKeydown });
  }

  bindMain(container, main, thumbs, thumbSlides, handlers = {}) {
    const setActiveThumb = (nextIndex = 0) => {
      const activeIndex = Number(nextIndex) || 0;
      thumbSlides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.toggle("blaze-thumb-active", isActive);
        slide.setAttribute("aria-current", isActive ? "true" : "false");
      });

      // The strip is horizontal here, so paging it is what brings a thumb into view.
      if (thumbSlides.length > 1) {
        thumbs?.slider?.slideTo?.(activeIndex, 0);
      }
    };

    const onResize = () => setActiveThumb(main.slider?.activeIndex ?? 0);
    window.addEventListener("resize", onResize, { passive: true });

    const bind = () => {
      const slider = main.slider;
      if (!slider) return false;

      const onSliderChange = ({ activeIndex } = {}) => setActiveThumb(activeIndex ?? 0);
      const onDomSlideChange = (event) => setActiveThumb(event?.detail?.activeIndex ?? 0);

      slider.on?.("slideChange", onSliderChange);
      slider.on?.("activeIndexChange", onSliderChange);
      main.addEventListener("slidechange", onDomSlideChange);
      main.addEventListener("activeindexchange", onDomSlideChange);

      setActiveThumb(slider.activeIndex ?? 0);

      // option-selectors.js and the lightbox both resolve the gallery this way.
      window.gallery_slider = slider;
      if (thumbs?.slider) window.gallery_thumb_slider = thumbs.slider;

      this.instances.set(container, {
        main,
        thumbs,
        thumbSlides,
        slider,
        onSliderChange,
        onDomSlideChange,
        onResize,
        ...handlers,
      });

      this.markReady(container);
      return true;
    };

    if (bind()) return;

    // The controller's DOM watcher may not have reached these yet. Nudge it, and
    // give up rather than spinning forever if the slider never appears.
    let attempts = 0;
    const attemptBind = () => {
      if (bind()) return;
      attempts += 1;
      window.themeBlazeSliderController?.refresh?.(main);
      if (thumbs) window.themeBlazeSliderController?.refresh?.(thumbs);
      if (attempts >= 180) {
        this.markReady(container);
        return;
      }
      requestAnimationFrame(attemptBind);
    };
    requestAnimationFrame(attemptBind);
  }

  markReady(container) {
    container.classList.remove("opacity-0");
    container.classList.add("opacity-1");
  }

  destroy(root = document) {
    const scope = root && typeof root.querySelectorAll === "function" ? root : document;
    Array.from(scope.querySelectorAll("[gallery-container]")).forEach((container) => {
      const instance = this.instances.get(container);
      if (!instance) return;

      instance.thumbSlides.forEach((slide) => {
        if (instance.onThumbClick) slide.removeEventListener("click", instance.onThumbClick);
        if (instance.onThumbKeydown) slide.removeEventListener("keydown", instance.onThumbKeydown);
      });

      instance.slider?.off?.("slideChange", instance.onSliderChange);
      instance.slider?.off?.("activeIndexChange", instance.onSliderChange);
      instance.main.removeEventListener("slidechange", instance.onDomSlideChange);
      instance.main.removeEventListener("activeindexchange", instance.onDomSlideChange);
      window.removeEventListener("resize", instance.onResize);

      container.classList.remove("opacity-1");
      container.classList.add("opacity-0");
      this.instances.delete(container);
    });
  }
}

export const productGallerySliderController = new ProductGallerySliderController(document);

if (typeof document !== "undefined") {
  window.themeProductGallerySliderController = productGallerySliderController;

  document.addEventListener("shopify:section:load", (event) => {
    productGallerySliderController.init(event?.target || document);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    productGallerySliderController.destroy(event?.target || document);
  });
}
